import { ZION_BUILD } from '../buildInfo.ts'
import { CURRENT_CLIENT_VERSION } from '../clientVersion.ts'
import { isCapacitorNative, resolveWebAppUrl } from '../api/ZionServer.ts'
import { isAppleDevice } from '../ui/layout.ts'
import { storedBuild, markBuildLoaded } from './appUpdate.ts'

const BUILD_KEY = 'zion_web_build'
const RELOAD_GUARD_PREFIX = 'zion_silent_reload_'

/** Web tab, PWA Android/desktop, Capacitor Android — mai iOS. */
export function shouldRunSilentWebUpdate(): boolean {
  if (isAppleDevice()) return false
  try {
    const Cap = (
      window as unknown as { Capacitor?: { getPlatform?: () => string } }
    ).Capacitor
    if (Cap?.getPlatform?.() === 'ios') return false
  } catch {
    /* ignore */
  }
  return true
}

export function normalizeBuildId(raw: unknown): string | null {
  const id = String(raw ?? '')
    .trim()
    .replace(/^v/i, '')
  if (!id || id === 'dev') return null
  if (/^\d{12,14}$/.test(id)) return id
  return null
}

export function compareBuildId(a: string, b: string): number {
  const na = normalizeBuildId(a)
  const nb = normalizeBuildId(b)
  if (!na && !nb) return 0
  if (!na) return -1
  if (!nb) return 1
  if (na.length !== nb.length) return na.length > nb.length ? 1 : -1
  if (na === nb) return 0
  return na > nb ? 1 : -1
}

type VersionPayload = {
  version?: string
  webBuild?: string
  bundleVersion?: string
  build?: string
}

async function fetchJson(url: string, timeoutMs = 4_000): Promise<VersionPayload | null> {
  try {
    const ctrl = new AbortController()
    const timer = window.setTimeout(() => ctrl.abort(), timeoutMs)
    const res = await fetch(url, {
      cache: 'no-store',
      credentials: 'omit',
      signal: ctrl.signal,
    })
    window.clearTimeout(timer)
    if (!res.ok) return null
    return (await res.json()) as VersionPayload
  } catch {
    return null
  }
}

/** Versione remota del bundle web — `/api/version` poi fallback `build.json`. */
export async function fetchRemoteWebBuild(): Promise<string | null> {
  const bust = Date.now()
  const api = await fetchJson(resolveWebAppUrl(`/api/version?_=${bust}`))
  const fromApi = normalizeBuildId(
    api?.webBuild ?? api?.bundleVersion ?? api?.build,
  )
  if (fromApi) return fromApi

  const manifest = await fetchJson(resolveWebAppUrl(`/app/build.json?_=${bust}`))
  return normalizeBuildId(manifest?.webBuild)
}

function reloadGuardKey(remoteBuild: string): string {
  return `${RELOAD_GUARD_PREFIX}${remoteBuild}`
}

function canReloadFor(remoteBuild: string): boolean {
  try {
    if (sessionStorage.getItem(reloadGuardKey(remoteBuild))) return false
    sessionStorage.setItem(reloadGuardKey(remoteBuild), '1')
    return true
  } catch {
    return true
  }
}

function waitForControllerChange(ms: number): Promise<void> {
  return new Promise((resolve) => {
    if (!('serviceWorker' in navigator)) {
      resolve()
      return
    }
    let done = false
    const finish = (): void => {
      if (done) return
      done = true
      navigator.serviceWorker.removeEventListener('controllerchange', onChange)
      resolve()
    }
    const onChange = (): void => finish()
    navigator.serviceWorker.addEventListener('controllerchange', onChange)
    window.setTimeout(finish, ms)
  })
}

async function prefetchCoreAssets(remoteBuild: string): Promise<void> {
  if (!('caches' in window)) return
  try {
    const cache = await caches.open(`zion-pwa-${remoteBuild}`)
    await Promise.all(
      [
        `/app/assets/app.js?v=${remoteBuild}`,
        `/app/assets/app.css?v=${remoteBuild}`,
        `/app/sw.js?v=${remoteBuild}`,
      ].map((path) => cache.add(resolveWebAppUrl(path)).catch(() => undefined)),
    )
  } catch {
    /* ignore */
  }
}

async function applySilentUpdate(remoteBuild: string): Promise<void> {
  try {
    localStorage.setItem(BUILD_KEY, remoteBuild)
  } catch {
    /* ignore */
  }

  if (isCapacitorNative()) {
    window.location.replace(
      `https://www.zionyx.ch/app/?v=${encodeURIComponent(remoteBuild)}&_silent=1`,
    )
    return
  }

  if ('serviceWorker' in navigator) {
    try {
      let reg = (await navigator.serviceWorker.getRegistration('/app/')) ?? null
      if (!reg) {
        const { registerServiceWorker } = await import('../push/webPush.ts')
        reg = (await registerServiceWorker()) ?? null
      }
      if (reg) {
        await reg.update().catch(() => undefined)
        const waiting = reg.waiting
        if (waiting) {
          waiting.postMessage({ type: 'SKIP_WAITING' })
        }
        await waitForControllerChange(2_500)
      }
    } catch {
      /* ignore */
    }
  }

  await prefetchCoreAssets(remoteBuild)

  const url = new URL(window.location.href)
  url.searchParams.set('v', remoteBuild)
  url.searchParams.set('_silent', '1')
  window.location.replace(url.toString())
}

/** Applica update SW senza banner (web/PWA non-iOS). */
export function silentReloadForWaitingWorker(worker: ServiceWorker | null | undefined): void {
  if (!shouldRunSilentWebUpdate()) return
  if (worker) {
    worker.postMessage({ type: 'SKIP_WAITING' })
    return
  }
  void navigator.serviceWorker.getRegistration('/app/').then((reg) => {
    reg?.waiting?.postMessage({ type: 'SKIP_WAITING' })
  })
}

/**
 * Controllo silenzioso al boot.
 * @returns true se sta per ricaricare — interrompere il boot.
 */
export async function runSilentAutoUpdateOnLaunch(): Promise<boolean> {
  if (!shouldRunSilentWebUpdate()) return false

  const remote = await fetchRemoteWebBuild()
  if (!remote) return false

  const stored = storedBuild()
  const current = normalizeBuildId(ZION_BUILD)

  if (current && remote === current) {
    markBuildLoaded()
    return false
  }

  if (stored && compareBuildId(remote, stored) <= 0) {
    return false
  }

  if (!canReloadFor(remote)) {
    return false
  }

  console.log('[ZION] silent update', { remote, stored, current, client: CURRENT_CLIENT_VERSION })
  await applySilentUpdate(remote)
  return true
}
